import { useEffect, useRef, useState } from 'react';
import type { FlowSideBasisSelection } from 'utils/shared/trades/presets';
import {
  getFlowSideBasisCacheKey,
  getTradeSidesForFlowSideBasis,
} from 'utils/shared/trades/flowSideBasis';

export const FLOW_SIDE_PREVIEW_DEBOUNCE_MS = 750;

interface UseFlowSidePreviewCountArgs {
  /** Whether the command center modal is open. */
  isOpen: boolean;
  /** The side basis being edited in the modal draft. */
  draftFlowSideBasis: FlowSideBasisSelection;
  /** The side basis currently applied to the table. */
  committedFlowSideBasis: FlowSideBasisSelection;
  /**
   * Fetches the previewed side's rows into a dataset that ONLY feeds the
   * command center count — never the committed table feed. Pass `undefined`
   * on surfaces that can't (or shouldn't) preview; that disables the preview.
   */
  requestPreview?: (tradeSides: string[]) => void;
  /** Whether the preview fetch is in flight. */
  isFetchingPreview?: boolean;
  debounceMs?: number;
}

interface UseFlowSidePreviewCountResult {
  /** True when the draft side differs from the applied side and we can preview it. */
  isPreviewActive: boolean;
  /** True while debouncing or fetching the previewed side — show a skeleton on the count. */
  isCountLoading: boolean;
}

/**
 * Coordinates the command center's "live count" when the user changes the trade
 * side inside the modal. The previewed side's rows are fetched into a SEPARATE
 * dataset (via `requestPreview`) so the committed table feed is never touched —
 * Apply/Cancel can't leave the table showing the wrong side's data.
 *
 * The fetch is debounced so rapid side toggling fires a single request once the
 * selection settles, and `isCountLoading` stays true until the data for the
 * current draft side has actually landed.
 */
export function useFlowSidePreviewCount({
  isOpen,
  draftFlowSideBasis,
  committedFlowSideBasis,
  requestPreview,
  isFetchingPreview = false,
  debounceMs = FLOW_SIDE_PREVIEW_DEBOUNCE_MS,
}: UseFlowSidePreviewCountArgs): UseFlowSidePreviewCountResult {
  const draftKey = getFlowSideBasisCacheKey(draftFlowSideBasis);
  const committedKey = getFlowSideBasisCacheKey(committedFlowSideBasis);
  const isPreviewActive = isOpen && !!requestPreview && draftKey !== committedKey;

  // Hold the latest values in refs so they don't become effect deps — otherwise an
  // unrelated re-render would reset the debounce timer and it might never fire.
  const requestPreviewRef = useRef(requestPreview);
  requestPreviewRef.current = requestPreview;
  const draftBasisRef = useRef(draftFlowSideBasis);
  draftBasisRef.current = draftFlowSideBasis;

  // The side key we've asked the page to fetch, and the key whose rows have landed.
  const [requestedKey, setRequestedKey] = useState<string | null>(null);
  const [loadedKey, setLoadedKey] = useState<string | null>(null);

  // Debounced request: fire once the draft side settles.
  useEffect(() => {
    if (!isPreviewActive || requestedKey === draftKey) {
      return;
    }

    const timer = setTimeout(() => {
      setRequestedKey(draftKey);
      requestPreviewRef.current?.(getTradeSidesForFlowSideBasis(draftBasisRef.current));
    }, debounceMs);

    return () => clearTimeout(timer);
  }, [isPreviewActive, draftKey, requestedKey, debounceMs]);

  // Mark the previewed side "loaded" once its fetch finishes (success or error),
  // so the skeleton clears even if the request fails.
  const wasFetchingRef = useRef(false);
  useEffect(() => {
    if (wasFetchingRef.current && !isFetchingPreview && requestedKey) {
      setLoadedKey(requestedKey);
    }
    wasFetchingRef.current = isFetchingPreview;
  }, [isFetchingPreview, requestedKey]);

  // Reset bookkeeping when preview turns off (modal closed or draft matches applied).
  useEffect(() => {
    if (!isPreviewActive) {
      setRequestedKey(null);
      setLoadedKey(null);
    }
  }, [isPreviewActive]);

  const isCountLoading = isPreviewActive && (isFetchingPreview || draftKey !== loadedKey);

  return { isPreviewActive, isCountLoading };
}
